// Sorting and filtering of the Updates table (05.10.2026). The columns were
// copied from a host table: they compared text lengths (all ids have the
// same length, so nothing moved), subtracted strings, filtered on "ipv4" and
// offered "System"/"User" as versions. Pure functions over the rows.

export interface UpdateRow {
  uuid: string;
  active?: number | null;
  archived?: boolean | null;
  software?: string | null;
  version?: string | null;
  file?: string | null;
  precessor?: string | null;
}

/** Text order, case-insensitive; empty values last. */
export const compareText = (a?: string | null, b?: string | null): number =>
  !a ? (!b ? 0 : 1) : !b ? -1 : a.localeCompare(b, undefined, { sensitivity: 'base' });

const versionParts = (v?: string | null): number[] =>
  (v ?? '').split('.').map((p) => Number.parseInt(p, 10)).map((n) => (Number.isNaN(n) ? -1 : n));

/** Version order part by part: 6.99 before 6.714 before 7.763; empty first. */
export const compareVersion = (a?: string | null, b?: string | null): number => {
  const pa = versionParts(a);
  const pb = versionParts(b);
  const length = Math.max(pa.length, pb.length);
  for (let i = 0; i < length; i++) {
    const diff = (pa[i] ?? -1) - (pb[i] ?? -1);
    if (diff !== 0) return diff;
  }
  return 0;
};

/** The major version a filter groups by: "7" for 7.763. */
export const majorOf = (v?: string | null): string => (v ?? '').split('.')[0] ?? '';

/** One filter entry per major version present, newest first. */
export const majorFilters = (rows: readonly UpdateRow[]): { text: string; value: string }[] =>
  [...new Set(rows.map((r) => majorOf(r.version)).filter((m) => m !== ''))]
    .sort((a, b) => compareVersion(b, a))
    .map((m) => ({ text: m + '.x', value: m }));

/** What the predecessor column shows: the predecessor's version if it is in the table, else its id. */
export const predecessorLabel = (rows: readonly UpdateRow[]) => {
  const versions = new Map(rows.map((r) => [r.uuid, r.version ?? '']));
  return (precessor?: string | null): string =>
    !precessor ? '' : versions.get(precessor) || precessor;
};

// Archiving (08.10.2026). A release is archived, never deleted: a spoke on it
// is still found by its row, and the chain runs through it. The hub refuses
// to archive an active release; the page does not offer it.

/** Whether the row is archived. */
export const isArchived = (row: UpdateRow): boolean => row.archived === true;

/** Whether the row may be archived: inactive and not archived yet. */
export const mayArchive = (row: UpdateRow): boolean => !isArchived(row) && (row.active ?? 0) === 0;

/** The rows the table shows: archived ones only when asked for. */
export const visibleRows = <T extends UpdateRow>(rows: readonly T[], showArchived: boolean): T[] =>
  rows.filter((r) => showArchived || !isArchived(r));

/**
 * The selected rows that may be archived, in version order - what the bulk
 * action sends and its confirm dialog lists. Ids no longer in the table,
 * active and archived rows drop out.
 */
export const archivableSelection = <T extends UpdateRow>(rows: readonly T[], selected: readonly string[]): T[] => {
  const wanted = new Set(selected);
  return rows
    .filter((r) => wanted.has(r.uuid) && mayArchive(r))
    .sort((a, b) => compareVersion(a.version, b.version));
};
